import React, { useState, useEffect, useCallback, useContext } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  MenuItem,
  Select,
  Pagination,
  InputAdornment,
  Tooltip,
  Switch,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  KeyboardArrowRight as ArrowIcon,
  TuneRounded as TuneIcon,
  DiamondOutlined as DiamondIcon,
  NotificationsNoneRounded as BellIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import axios from "axios";
import patternIng from "../../assets/banner/pattern.png";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";

/* ─── CONSTANTS ──────────────────────────────────────────────── */
const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const FY_OPTIONS = ["24-25", "25-26", "26-27", "27-28", "28-29"];
const FY_FORMAT_OPTIONS = ["YY-YY", "YYYY-YYYY"];
const SEPARATOR_OPTIONS = ["-", "/", "_", "."];
const ITEMS = 7;

const emptyForm = {
  id: null,
  metalTypeId: "",
  prefixMasterId: "",
  prefixValue: "",
  separator: "-",
  serialLength: 5,
  includeFy: true,
  fyFormat: "YY-YY",
  currentFy: "26-27",
  resetOnFy: true,
  isActive: true,
};

/* ─── SHARED SX ─────────────────────────────────────────────── */
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "#FFFFFF",
    borderRadius: "10px",
    fontSize: "0.83rem",
    color: "#0F1724",
    transition: "all 0.18s",
    "& fieldset": { borderColor: "#E8ECF0", borderWidth: "1.5px" },
    "&:hover fieldset": { borderColor: "#D1D9E0" },
    "&.Mui-focused fieldset": {
      borderColor: "#1a1a1a",
      borderWidth: "2px",
      boxShadow: "0 0 0 3px rgba(26,26,26,0.1)",
    },
  },
  "& input": { color: "#0F1724", fontSize: "0.83rem" },
  "& input::placeholder": { color: "#9AA3B2", opacity: 1 },
  "& textarea": { color: "#0F1724", fontSize: "0.83rem" },
  "& textarea::placeholder": { color: "#9AA3B2", opacity: 1 },
};

const monoFieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "rgba(26,26,26,0.03)",
    borderRadius: "10px",
    fontSize: "0.83rem",
    transition: "all 0.18s",
    "& fieldset": { borderColor: "#D1D9E0", borderWidth: "1.5px" },
    "&:hover fieldset": { borderColor: "#1a1a1a" },
    "&.Mui-focused fieldset": {
      borderColor: "#1a1a1a",
      borderWidth: "2px",
      boxShadow: "0 0 0 3px rgba(26,26,26,0.1)",
    },
  },
  "& input": {
    color: "#1a1a1a",
    fontSize: "0.8rem",
    fontFamily: "monospace",
    letterSpacing: "0.04em",
    fontWeight: 700,
  },
};

const selectSx = {
  bgcolor: "#FFFFFF",
  borderRadius: "10px",
  fontSize: "0.83rem",
  color: "#0F1724",
  transition: "all 0.18s",
  "& fieldset": { borderColor: "#E8ECF0", borderWidth: "1.5px" },
  "&:hover fieldset": { borderColor: "#D1D9E0" },
  "&.Mui-focused fieldset": {
    borderColor: "#1a1a1a",
    borderWidth: "2px",
    boxShadow: "0 0 0 3px rgba(26,26,26,0.1)",
  },
  "& .MuiSvgIcon-root": { color: "#9AA3B2" },
};

const menuPropsSx = {
  PaperProps: {
    sx: {
      bgcolor: "#FFFFFF",
      border: "1px solid #E8ECF0",
      borderRadius: "12px",
      boxShadow: "0 8px 40px rgba(15,23,36,0.12)",
      mt: 0.5,
      "& .MuiMenuItem-root": {
        fontSize: "0.83rem",
        color: "#5A6478",
        py: 1,
        borderRadius: "8px",
        mx: 0.5,
        "&:hover": { bgcolor: "rgba(26,26,26,0.05)", color: "#1a1a1a" },
        "&.Mui-selected": {
          bgcolor: "rgba(26,26,26,0.05)",
          color: "#1a1a1a",
          fontWeight: 700,
        },
      },
    },
  },
};

/* ─── MICRO COMPONENTS ──────────────────────────────────────── */
const Label = ({ children, required }) => (
  <Typography
    sx={{
      fontSize: "0.7rem",
      fontWeight: 600,
      color: "#5A6478",
      mb: 0.7,
      letterSpacing: "0.01em",
    }}
  >
    {children}
    {required && (
      <Box component="span" sx={{ color: "#1a1a1a", ml: 0.3 }}>
        *
      </Box>
    )}
  </Typography>
);

const SectionHead = ({ label, description }) => (
  <Box sx={{ mb: 2.5, mt: 0.5 }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        mb: description ? 0.4 : 0,
      }}
    >
      <Box
        sx={{ height: "2px", width: 18, bgcolor: "#1a1a1a", borderRadius: 1 }}
      />
      <Typography
        sx={{
          fontSize: "0.65rem",
          fontWeight: 700,
          letterSpacing: "0.14em",
          color: "#1a1a1a",
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, height: "1px", bgcolor: "#E8ECF0" }} />
    </Box>
    {description && (
      <Typography sx={{ fontSize: "0.72rem", color: "#9AA3B2", pl: "37px" }}>
        {description}
      </Typography>
    )}
  </Box>
);

const StatusPill = ({ status }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.6,
      px: 1.2,
      py: 0.4,
      borderRadius: "20px",
      bgcolor: status ? "#DCFCE7" : "#F3F4F6",
      border: `1px solid ${status ? "#BBF7D0" : "#E8ECF0"}`,
    }}
  >
    <Box
      sx={{
        width: 5,
        height: 5,
        borderRadius: "50%",
        bgcolor: status ? "#16A34A" : "#9AA3B2",
        ...(status && {
          animation: "blink 2s ease-in-out infinite",
          "@keyframes blink": {
            "0%,100%": { opacity: 1 },
            "50%": { opacity: 0.35 },
          },
        }),
      }}
    />
    <Typography
      sx={{
        fontSize: "0.62rem",
        fontWeight: 700,
        color: status ? "#16A34A" : "#9AA3B2",
        letterSpacing: "0.04em",
      }}
    >
      {status ? "Active" : "Inactive"}
    </Typography>
  </Box>
);

const StatCard = ({ label, value, accent, note, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      flex: 1,
      px: 2.5,
      py: 2,
      bgcolor: "#FFFFFF",
      border: "1px solid #E8ECF0",
      borderRadius: "14px",
      transition: "all 0.2s",
      cursor: onClick ? "pointer" : "default",
      "&:hover": {
        borderColor: "#D1D9E0",
        boxShadow: "0 4px 20px rgba(26,26,26,0.07)",
      },
    }}
  >
    <Typography
      sx={{
        fontSize: "0.62rem",
        fontWeight: 600,
        color: "#9AA3B2",
        letterSpacing: "0.1em",
        mb: 0.6,
        textTransform: "uppercase",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: "1.75rem",
        fontWeight: 800,
        color: accent || "#0F1724",
        lineHeight: 1,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value}
    </Typography>
    {note && (
      <Typography sx={{ fontSize: "0.65rem", color: "#9AA3B2", mt: 0.4 }}>
        {note}
      </Typography>
    )}
  </Box>
);

/* ─── MAIN COMPONENT ─────────────────────────────────────────── */
export default function PrefixConfiguration() {
  const { userInfo } = useContext(AutoTaskAuthContext) || {};
  const user_id = userInfo?.id;
  const store_id = userInfo?.store_id;
  const location = useLocation();

  const [mounted, setMounted] = useState(false);
  const [matrixData, setMatrixData] = useState([]); // full prefix-matrix response
  const [allPrefixMasters, setAllPrefixMasters] = useState([]); // /prefix-master list
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [search, setSearch] = useState("");
  const [metalFilter, setMetalFilter] = useState("ALL");
  const [prefixFilter, setPrefixFilter] = useState("ALL");
  const [assignedPage, setAssignedPage] = useState(1);
  const [unassignedPage, setUnassignedPage] = useState(1);
  const [loadingMatrix, setLoadingMatrix] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    setTimeout(() => setMounted(true), 60);
  }, []);

  useEffect(() => {
    if (store_id !== null && store_id !== undefined) {
      fetchMatrix();
      fetchPrefixMasters();
    }
  }, [store_id]);

  const fetchMatrix = useCallback(async () => {
    setLoadingMatrix(true);
    try {
      const res = await axios.get(
        `${BASE_URL}/api/jewel-master/metal-type-master/store/${store_id}/prefix-matrix`,
      );
      if (res.data?.success) setMatrixData(res.data.data || []);
    } catch {
      showSnackbar("Failed to fetch prefix matrix", "error");
    } finally {
      setLoadingMatrix(false);
    }
  }, [store_id]);

  const fetchPrefixMasters = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/jewel-master/metal-prefix-config`,
      );
      if (res.data?.success) setAllPrefixMasters(res.data.data || []);
    } catch {
      showSnackbar("Failed to fetch prefix masters", "error");
    }
  };

  /* ─── FLATTEN rows from matrix ─── */
  // Each row = { metalId, metalName, metalCode, prefixId, prefixName, prefixDesc, config, isAssigned }
  const allRows = [];
  matrixData.forEach((metal) => {
    (metal.prefixes || []).forEach((prefix) => {
      allRows.push({
        metalId: metal.id,
        metalName: metal.name,
        metalCode: metal.code,
        prefixMasterId: prefix.id,
        prefixName: prefix.name,
        prefixDesc: prefix.description,
        isConfig: prefix.is_config,
        config: prefix.config,
      });
    });
  });

  /* ─── FILTER ─── */
  const filterRow = (row) => {
    const q = search.toLowerCase();
    const matchQ =
      !q ||
      row.metalName?.toLowerCase().includes(q) ||
      row.prefixName?.toLowerCase().includes(q) ||
      row.config?.prefix_value?.toLowerCase().includes(q);
    const matchMetal = metalFilter === "ALL" || row.metalId === metalFilter;
    const matchPrefix =
      prefixFilter === "ALL" || row.prefixMasterId === prefixFilter;
    return matchQ && matchMetal && matchPrefix;
  };

  const assignedRows = allRows.filter((r) => r.isConfig && filterRow(r));
  const unassignedRows = allRows.filter((r) => !r.isConfig && filterRow(r));

  const assignedPages = Math.ceil(assignedRows.length / ITEMS) || 1;
  const unassignedPages = Math.ceil(unassignedRows.length / ITEMS) || 1;
  const paginatedAssigned = assignedRows.slice(
    (assignedPage - 1) * ITEMS,
    assignedPage * ITEMS,
  );
  const paginatedUnassigned = unassignedRows.slice(
    (unassignedPage - 1) * ITEMS,
    unassignedPage * ITEMS,
  );

  /* ─── STATS ─── */
  const totalAssigned = allRows.filter((r) => r.isConfig).length;
  const totalUnassigned = allRows.filter((r) => !r.isConfig).length;
  const uniqueMetals = new Set(
    allRows.filter((r) => r.isConfig).map((r) => r.metalId),
  ).size;

  /* ─── DISTINCT metal/prefix lists for filter chips ─── */
  const uniqueMetalList = matrixData.map((m) => ({
    id: m.id,
    name: m.name,
    code: m.code,
  }));
  const uniquePrefixList = allPrefixMasters;

  /* ─── FORM HANDLERS ─── */
  const openAdd = (row = null) => {
    setEditMode(false);
    setSelectedConfigId(null);
    setFormData({
      ...emptyForm,
      metalTypeId: row?.metalId || "",
      prefixMasterId: row?.prefixMasterId || "",
    });
    setIsFormOpen(true);
  };

  const openEdit = (row) => {
    const c = row.config;
    setEditMode(true);
    setSelectedConfigId(c.id);
    setFormData({
      id: c.id,
      metalTypeId: row.metalId,
      prefixMasterId: row.prefixMasterId,
      prefixValue: c.prefix_value || "",
      separator: c.separator || "-",
      serialLength: c.serial_length || 5,
      includeFy: c.include_fy ?? true,
      fyFormat: c.fy_format || "YY-YY",
      currentFy: c.current_fy || "25-26",
      resetOnFy: c.reset_on_fy ?? true,
      isActive: c.is_active ?? true,
    });
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setSelectedConfigId(null);
    setFormData(emptyForm);
  };

  const set = (k, v) => setFormData((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (
      !formData.metalTypeId ||
      !formData.prefixMasterId ||
      !formData.prefixValue.trim()
    ) {
      showSnackbar(
        "Metal type, prefix type and prefix value are required",
        "error",
      );
      return;
    }
    setSaving(true);
    try {
      const payload = {
        store_id,
        metal_type_id: formData.metalTypeId,
        prefix_master_id: formData.prefixMasterId,
        prefix_value: formData.prefixValue,
        separator: formData.separator,
        serial_length: formData.serialLength,
        include_fy: formData.includeFy,
        fy_format: formData.fyFormat,
        current_fy: formData.currentFy,
        reset_on_fy: formData.resetOnFy,
        is_active: formData.isActive,
        created_by: user_id,
        updated_by: user_id,
      };

      const url = editMode
        ? `${BASE_URL}/api/jewel-master/metal-prefix-config/${formData.id}`
        : `${BASE_URL}/api/jewel-master/metal-prefix-config`;

      const res = editMode
        ? await axios.put(url, payload)
        : await axios.post(url, payload);

      if (!res.data?.success)
        throw new Error(res.data?.message || "Save failed");

      showSnackbar(
        editMode
          ? "Prefix configuration updated"
          : "Prefix configuration created",
        "success",
      );

      await fetchMatrix();
      closeForm();
    } catch (err) {
      showSnackbar(err.message || "Something went wrong", "error");
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const buildPreview = (data) => {
    const serial = String(1).padStart(data.serialLength, "0");
    return [data.prefixValue, data.includeFy ? data.currentFy : null, serial]
      .filter(Boolean)
      .join(data.separator);
  };

  /* ─── TABLE HEADER COLS ─── */
  const assignedCols = isFormOpen
    ? ["Metal", "Prefix Type", "Value", "Preview", "Status"]
    : [
        "Metal",
        "Prefix Type",
        "Prefix Value",
        "FY",
        "Serial Len",
        "Preview",
        "Status",
        "",
      ];

  const unassignedCols = isFormOpen
    ? ["Metal", "Prefix Type", ""]
    : ["Metal", "Prefix Type", "Description", ""];

  /* ─── RENDER ─── */
  return (
    <Box
      sx={{
        height: "100vh",
        bgcolor: "#F7F8FA",
        display: "flex",
        flexDirection: "column",
        fontFamily: "'Inter', -apple-system, sans-serif",
        opacity: mounted ? 1 : 0,
        transition: "opacity 0.4s ease",
        overflow: "hidden",
      }}
    >
      {/* TOPBAR */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3.5,
          py: "13px",
          bgcolor: "#FFFFFF",
          borderBottom: "1px solid #E8ECF0",
          flexShrink: 0,
          zIndex: 50,
        }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "11px",
              bgcolor: "#000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 14px rgba(150,150,150,0.35)",
              mb: 2,
            }}
          >
            <DiamondIcon sx={{ fontSize: 18, color: "#ffc400" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.68rem", color: "#9AA3B2", mb: 0.1 }}>
              Inventory Control ›{" "}
              <Box component="span" sx={{ color: "#daab14", fontWeight: 600 }}>
                Prefix Config
              </Box>
            </Typography>
            <Typography
              sx={{
                fontSize: "1.15rem",
                fontWeight: 800,
                color: "#0F1724",
                letterSpacing: "-0.02em",
              }}
            >
              Prefix Configuration
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <TextField
            placeholder="Search by metal, prefix, value…"
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setAssignedPage(1);
              setUnassignedPage(1);
            }}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": {
                bgcolor: "#F7F8FA",
                borderRadius: "10px",
                fontSize: "0.82rem",
                "& fieldset": { borderColor: "#E8ECF0" },
                "&:hover fieldset": { borderColor: "#D1D9E0" },
                "&.Mui-focused fieldset": {
                  borderColor: "#1a1a1a",
                  boxShadow: "0 0 0 3px rgba(26,26,26,0.1)",
                },
              },
              "& input": { color: "#0F1724", py: "8px" },
              "& input::placeholder": { color: "#9AA3B2", opacity: 1 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9AA3B2", fontSize: 17 }} />
                </InputAdornment>
              ),
            }}
          />
          <IconButton
            sx={{
              bgcolor: "#F7F8FA",
              border: "1px solid #E8ECF0",
              borderRadius: "10px",
              p: "7px",
              "&:hover": {
                bgcolor: "rgba(26,26,26,0.05)",
                borderColor: "#D1D9E0",
              },
            }}
          >
            <TuneIcon sx={{ fontSize: 17, color: "#5A6478" }} />
          </IconButton>
          <IconButton
            sx={{
              bgcolor: "#F7F8FA",
              border: "1px solid #E8ECF0",
              borderRadius: "10px",
              p: "7px",
              "&:hover": {
                bgcolor: "rgba(26,26,26,0.05)",
                borderColor: "#D1D9E0",
              },
            }}
          >
            <BellIcon sx={{ fontSize: 17, color: "#5A6478" }} />
          </IconButton>
          <Button
            onClick={() => openAdd()}
            startIcon={<AddIcon sx={{ fontSize: "15px !important" }} />}
            sx={{
              background: "linear-gradient(135deg, #1a1a1a 0%, #000000 100%)",
              color: "#D4AF37",
              px: 2.5,
              py: 1,
              borderRadius: "10px",
              fontWeight: 700,
              fontSize: "0.8rem",
              textTransform: "none",
              boxShadow: "0 4px 14px rgba(26,26,26,0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
                boxShadow: "0 6px 20px rgba(26,26,26,0.4)",
              },
              transition: "all 0.2s",
            }}
          >
            Add Configuration
          </Button>
        </Box>
      </Box>

      {/* CONTENT ROW */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* TABLE PANEL */}
        <Box
          sx={{
            width: isFormOpen ? "50%" : "100%",
            transition: "width 0.45s cubic-bezier(0.4,0,0.2,1)",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            px: isFormOpen ? 2.5 : 3.5,
            pt: 3,
            pb: 3,
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {/* Stats row */}
          {!isFormOpen && (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mb: 3,
                animation: "fadeUp 0.35s ease",
                "@keyframes fadeUp": {
                  from: { opacity: 0, transform: "translateY(8px)" },
                  to: { opacity: 1, transform: "translateY(0)" },
                },
              }}
            >
              <StatCard
                label="Assigned Prefixes"
                value={totalAssigned}
                note="configured"
              />
              <StatCard
                label="Unassigned"
                value={totalUnassigned}
                accent="#D97706"
                note="pending setup"
              />
              <StatCard
                label="Metal Types"
                value={matrixData.length}
                accent="#1a1a1a"
                note="in matrix"
              />
              <StatCard
                label="Configured Metals"
                value={uniqueMetals}
                accent="#16A34A"
                note="with prefixes"
              />
            </Box>
          )}

          {/* Metal Filter Chips */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mb: 1.5,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.68rem",
                fontWeight: 600,
                color: "#9AA3B2",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                mr: 0.5,
              }}
            >
              Metal:
            </Typography>
            {[{ id: "ALL", code: "All", name: "" }, ...uniqueMetalList].map(
              (m) => {
                const active = metalFilter === m.id;
                return (
                  <Box
                    key={m.id}
                    onClick={() => {
                      setMetalFilter(m.id);
                      setAssignedPage(1);
                      setUnassignedPage(1);
                    }}
                    sx={{
                      px: 1.5,
                      py: 0.4,
                      borderRadius: "8px",
                      cursor: "pointer",
                      bgcolor: active ? "#1a1a1a" : "#FFFFFF",
                      border: `1.5px solid ${active ? "#1a1a1a" : "#E8ECF0"}`,
                      transition: "all 0.15s",
                      "&:hover": {
                        borderColor: "#1a1a1a",
                        bgcolor: active ? "#1a1a1a" : "rgba(26,26,26,0.04)",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: active ? 700 : 500,
                        color: active ? "#D4AF37" : "#5A6478",
                      }}
                    >
                      {m.id === "ALL" ? "All Metals" : m.code}
                    </Typography>
                  </Box>
                );
              },
            )}
          </Box>

          {/* Prefix Filter Chips */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mb: 2,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.68rem",
                fontWeight: 600,
                color: "#9AA3B2",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                mr: 0.5,
              }}
            >
              Prefix:
            </Typography>
            {[{ id: "ALL", name: "All Prefixes" }, ...uniquePrefixList].map(
              (p) => {
                const active = prefixFilter === p.id;
                const shortCode =
                  p.description?.split(" - ")?.[0] || p.name?.split(" ")?.[0];
                return (
                  <Box
                    key={p.id}
                    onClick={() => {
                      setPrefixFilter(p.id);
                      setAssignedPage(1);
                      setUnassignedPage(1);
                    }}
                    sx={{
                      px: 1.5,
                      py: 0.4,
                      borderRadius: "8px",
                      cursor: "pointer",
                      bgcolor: active ? "#1a1a1a" : "#FFFFFF",
                      border: `1.5px solid ${active ? "#1a1a1a" : "#E8ECF0"}`,
                      transition: "all 0.15s",
                      "&:hover": {
                        borderColor: "#1a1a1a",
                        bgcolor: active ? "#1a1a1a" : "rgba(26,26,26,0.04)",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: active ? 700 : 500,
                        color: active ? "#D4AF37" : "#5A6478",
                      }}
                    >
                      {p.id === "ALL" ? "All Prefixes" : shortCode}
                    </Typography>
                  </Box>
                );
              },
            )}
          </Box>

          {loadingMatrix ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={28} sx={{ color: "#1a1a1a" }} />
            </Box>
          ) : (
            <>
              {/* ── ASSIGNED TABLE ── */}
              <Box sx={{ mb: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      height: "2px",
                      width: 18,
                      bgcolor: "#16A34A",
                      borderRadius: 1,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      letterSpacing: "0.14em",
                      color: "#16A34A",
                      textTransform: "uppercase",
                    }}
                  >
                    Assigned Prefixes
                  </Typography>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.2,
                      bgcolor: "#DCFCE7",
                      borderRadius: "6px",
                      border: "1px solid #BBF7D0",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.62rem",
                        fontWeight: 700,
                        color: "#16A34A",
                      }}
                    >
                      {assignedRows.length}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, height: "1px", bgcolor: "#E8ECF0" }} />
                </Box>

                <Box
                  sx={{
                    bgcolor: "#FFFFFF",
                    border: "1px solid #E8ECF0",
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 1px 8px rgba(15,23,36,0.05)",
                  }}
                >
                  <Box
                    sx={{
                      overflow: "auto",
                      scrollbarWidth: "none",
                      "&::-webkit-scrollbar": { display: "none" },
                    }}
                  >
                    <Table
                      stickyHeader
                      sx={{ minWidth: isFormOpen ? 380 : 820 }}
                    >
                      <TableHead>
                        <TableRow>
                          {assignedCols.map((h) => (
                            <TableCell
                              key={h}
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.62rem",
                                letterSpacing: "0.08em",
                                color: "#9AA3B2",
                                textTransform: "uppercase",
                                py: 1.5,
                                px: isFormOpen ? 1.8 : 2.5,
                                bgcolor: "#F7F8FA",
                                borderBottom: "1.5px solid #E8ECF0",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedAssigned.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={assignedCols.length}
                              sx={{
                                textAlign: "center",
                                py: 4,
                                color: "#9AA3B2",
                                fontSize: "0.8rem",
                              }}
                            >
                              No assigned prefixes found
                            </TableCell>
                          </TableRow>
                        ) : (
                          paginatedAssigned.map((row, i) => (
                            <TableRow
                              key={`${row.metalId}-${row.prefixMasterId}`}
                              onClick={() => openEdit(row)}
                              sx={{
                                cursor: "pointer",
                                animation: `rowIn 0.28s ease ${i * 0.03}s both`,
                                "@keyframes rowIn": {
                                  from: {
                                    opacity: 0,
                                    transform: "translateY(4px)",
                                  },
                                  to: {
                                    opacity: 1,
                                    transform: "translateY(0)",
                                  },
                                },
                                bgcolor:
                                  selectedConfigId === row.config?.id
                                    ? "rgba(26,26,26,0.04) !important"
                                    : "transparent",
                                "& td": {
                                  borderBottom: "1px solid #F7F8FA",
                                  transition: "background 0.12s",
                                },
                                "&:hover td": { bgcolor: "#F9FAFB" },
                                "&:last-child td": { borderBottom: "none" },
                              }}
                            >
                              {isFormOpen ? (
                                <>
                                  <TableCell sx={{ px: 1.8, py: 1.8 }}>
                                    <Typography
                                      sx={{
                                        fontWeight: 700,
                                        fontSize: "0.78rem",
                                        color: "#0F1724",
                                      }}
                                    >
                                      {row.metalName}
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: "0.62rem",
                                        color: "#9AA3B2",
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      {row.metalCode}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ px: 1.8 }}>
                                    <Typography
                                      sx={{
                                        fontSize: "0.75rem",
                                        color: "#5A6478",
                                      }}
                                    >
                                      {row.prefixName}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ px: 1.8 }}>
                                    <Box
                                      sx={{
                                        display: "inline-flex",
                                        px: 1,
                                        py: 0.3,
                                        bgcolor: "rgba(26,26,26,0.06)",
                                        borderRadius: "6px",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: "0.7rem",
                                          fontWeight: 700,
                                          color: "#1a1a1a",
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        {row.config?.prefix_value}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ px: 1.8 }}>
                                    <Box
                                      sx={{
                                        display: "inline-flex",
                                        px: 1,
                                        py: 0.3,
                                        bgcolor: "#FEF9E7",
                                        borderRadius: "6px",
                                        border: "1px solid #F5E6A3",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: "0.68rem",
                                          fontWeight: 700,
                                          color: "#D4AF37",
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        {[
                                          row.config?.prefix_value,
                                          row.config?.include_fy
                                            ? row.config?.current_fy
                                            : null,
                                          String(1).padStart(
                                            row.config?.serial_length || 5,
                                            "0",
                                          ),
                                        ]
                                          .filter(Boolean)
                                          .join(row.config?.separator || "-")}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ px: 1.8 }}>
                                    <StatusPill
                                      status={row.config?.is_active}
                                    />
                                  </TableCell>
                                </>
                              ) : (
                                <>
                                  <TableCell sx={{ px: 2.5, py: 2 }}>
                                    <Typography
                                      sx={{
                                        fontWeight: 700,
                                        fontSize: "0.85rem",
                                        color: "#0F1724",
                                      }}
                                    >
                                      {row.metalName}
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: "0.62rem",
                                        color: "#9AA3B2",
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      {row.metalCode}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ px: 2.5 }}>
                                    <Typography
                                      sx={{
                                        fontSize: "0.78rem",
                                        color: "#5A6478",
                                      }}
                                    >
                                      {row.prefixName}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ px: 2.5 }}>
                                    <Box
                                      sx={{
                                        display: "inline-flex",
                                        px: 1,
                                        py: 0.3,
                                        bgcolor: "rgba(26,26,26,0.06)",
                                        borderRadius: "6px",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontFamily: "monospace",
                                          fontSize: "0.75rem",
                                          color: "#1a1a1a",
                                          fontWeight: 700,
                                        }}
                                      >
                                        {row.config?.prefix_value}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ px: 2.5 }}>
                                    <Chip
                                      label={
                                        row.config?.current_fy
                                          ? row.config?.current_fy
                                          : "No FY"
                                      }
                                      size="small"
                                      sx={{
                                        height: 22,
                                        fontSize: "0.65rem",
                                        fontWeight: 700,
                                        bgcolor: row.config?.include_fy
                                          ? "rgba(26,26,26,0.06)"
                                          : "#F3F4F6",
                                        color: row.config?.include_fy
                                          ? "#1a1a1a"
                                          : "#9AA3B2",
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ px: 2.5 }}>
                                    <Typography
                                      sx={{
                                        fontFamily: "monospace",
                                        fontSize: "0.72rem",
                                        color: "#9AA3B2",
                                      }}
                                    >
                                      {row.config?.serial_length} digits
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ px: 2.5 }}>
                                    <Box
                                      sx={{
                                        display: "inline-flex",
                                        px: 1.2,
                                        py: 0.4,
                                        bgcolor: "#FEF9E7",
                                        borderRadius: "6px",
                                        border: "1px solid #F5E6A3",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontFamily: "monospace",
                                          fontSize: "0.72rem",
                                          color: "#D4AF37",
                                          fontWeight: 700,
                                        }}
                                      >
                                        {[
                                          row.config?.prefix_value,
                                          row.config?.include_fy
                                            ? row.config?.current_fy
                                            : null,
                                          String(1).padStart(
                                            row.config?.serial_length || 5,
                                            "0",
                                          ),
                                        ]
                                          .filter(Boolean)
                                          .join(row.config?.separator || "-")}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ px: 2.5 }}>
                                    <StatusPill
                                      status={row.config?.is_active}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ px: 2 }}>
                                    <Tooltip title="Edit" placement="left">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openEdit(row);
                                        }}
                                        sx={{
                                          bgcolor: "#F7F8FA",
                                          border: "1px solid #E8ECF0",
                                          borderRadius: "8px",
                                          p: "5px",
                                          "&:hover": {
                                            bgcolor: "rgba(26,26,26,0.05)",
                                            borderColor: "#D1D9E0",
                                          },
                                        }}
                                      >
                                        <EditIcon
                                          sx={{
                                            fontSize: 14,
                                            color: "#1a1a1a",
                                          }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </>
                              )}
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                  <Box
                    sx={{
                      px: 3,
                      py: 1.5,
                      display: "flex",
                      justifyContent: "flex-end",
                      borderTop: "1px solid #E8ECF0",
                    }}
                  >
                    <Pagination
                      count={assignedPages}
                      page={assignedPage}
                      onChange={(_, p) => setAssignedPage(p)}
                      size="small"
                      sx={{
                        "& .MuiPaginationItem-root": {
                          borderRadius: "8px",
                          fontSize: "0.75rem",
                          color: "#5A6478",
                          "&:hover": {
                            bgcolor: "rgba(26,26,26,0.05)",
                            color: "#1a1a1a",
                          },
                        },
                        "& .Mui-selected": {
                          bgcolor: "#1a1a1a !important",
                          color: "#D4AF37 !important",
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* ── UNASSIGNED TABLE ── */}
              <Box sx={{ mt: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      height: "2px",
                      width: 18,
                      bgcolor: "#D97706",
                      borderRadius: 1,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      letterSpacing: "0.14em",
                      color: "#D97706",
                      textTransform: "uppercase",
                    }}
                  >
                    Unassigned Prefixes
                  </Typography>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.2,
                      bgcolor: "#FEF3C7",
                      borderRadius: "6px",
                      border: "1px solid #FDE68A",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.62rem",
                        fontWeight: 700,
                        color: "#D97706",
                      }}
                    >
                      {unassignedRows.length}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, height: "1px", bgcolor: "#E8ECF0" }} />
                </Box>

                <Box
                  sx={{
                    bgcolor: "#FFFFFF",
                    border: "1px solid #E8ECF0",
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 1px 8px rgba(15,23,36,0.05)",
                  }}
                >
                  <Box
                    sx={{
                      overflow: "auto",
                      scrollbarWidth: "none",
                      "&::-webkit-scrollbar": { display: "none" },
                    }}
                  >
                    <Table
                      stickyHeader
                      sx={{ minWidth: isFormOpen ? 340 : 640 }}
                    >
                      <TableHead>
                        <TableRow>
                          {unassignedCols.map((h) => (
                            <TableCell
                              key={h}
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.62rem",
                                letterSpacing: "0.08em",
                                color: "#9AA3B2",
                                textTransform: "uppercase",
                                py: 1.5,
                                px: isFormOpen ? 1.8 : 2.5,
                                bgcolor: "#FFFBEB",
                                borderBottom: "1.5px solid #FDE68A",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedUnassigned.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={unassignedCols.length}
                              sx={{
                                textAlign: "center",
                                py: 4,
                                color: "#9AA3B2",
                                fontSize: "0.8rem",
                              }}
                            >
                              All prefixes assigned 🎉
                            </TableCell>
                          </TableRow>
                        ) : (
                          paginatedUnassigned.map((row, i) => (
                            <TableRow
                              key={`u-${row.metalId}-${row.prefixMasterId}`}
                              sx={{
                                animation: `rowIn 0.28s ease ${i * 0.03}s both`,
                                "@keyframes rowIn": {
                                  from: {
                                    opacity: 0,
                                    transform: "translateY(4px)",
                                  },
                                  to: {
                                    opacity: 1,
                                    transform: "translateY(0)",
                                  },
                                },
                                "& td": {
                                  borderBottom: "1px solid #FFFBEB",
                                  transition: "background 0.12s",
                                },
                                "&:hover td": { bgcolor: "#FFFDF5" },
                                "&:last-child td": { borderBottom: "none" },
                              }}
                            >
                              <TableCell
                                sx={{ px: isFormOpen ? 1.8 : 2.5, py: 1.8 }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: isFormOpen
                                      ? "0.78rem"
                                      : "0.85rem",
                                    color: "#0F1724",
                                  }}
                                >
                                  {row.metalName}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.62rem",
                                    color: "#9AA3B2",
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {row.metalCode}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ px: isFormOpen ? 1.8 : 2.5 }}>
                                <Typography
                                  sx={{ fontSize: "0.78rem", color: "#5A6478" }}
                                >
                                  {row.prefixName}
                                </Typography>
                              </TableCell>
                              {!isFormOpen && (
                                <TableCell sx={{ px: 2.5 }}>
                                  <Typography
                                    sx={{
                                      fontSize: "0.72rem",
                                      color: "#9AA3B2",
                                    }}
                                  >
                                    {row.prefixDesc}
                                  </Typography>
                                </TableCell>
                              )}
                              <TableCell sx={{ px: isFormOpen ? 1.8 : 2 }}>
                                <Tooltip
                                  title="Configure prefix"
                                  placement="left"
                                >
                                  <Button
                                    size="small"
                                    onClick={() => openAdd(row)}
                                    startIcon={
                                      <AddIcon
                                        sx={{ fontSize: "13px !important" }}
                                      />
                                    }
                                    sx={{
                                      bgcolor: "#1a1a1a",
                                      color: "#D4AF37",
                                      px: 1.5,
                                      py: 0.5,
                                      borderRadius: "8px",
                                      fontWeight: 700,
                                      fontSize: "0.7rem",
                                      textTransform: "none",
                                      minWidth: 0,
                                      whiteSpace: "nowrap",
                                      "&:hover": { bgcolor: "#333" },
                                    }}
                                  >
                                    {isFormOpen ? "" : "Configure"}
                                  </Button>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                  <Box
                    sx={{
                      px: 3,
                      py: 1.5,
                      display: "flex",
                      justifyContent: "flex-end",
                      borderTop: "1px solid #FDE68A",
                    }}
                  >
                    <Pagination
                      count={unassignedPages}
                      page={unassignedPage}
                      onChange={(_, p) => setUnassignedPage(p)}
                      size="small"
                      sx={{
                        "& .MuiPaginationItem-root": {
                          borderRadius: "8px",
                          fontSize: "0.75rem",
                          color: "#5A6478",
                          "&:hover": {
                            bgcolor: "rgba(26,26,26,0.05)",
                            color: "#1a1a1a",
                          },
                        },
                        "& .Mui-selected": {
                          bgcolor: "#1a1a1a !important",
                          color: "#D4AF37 !important",
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>

        {/* FORM PANEL */}
        {isFormOpen && (
          <Box
            sx={{
              boxShadow: "0px 0 20px #8d8d8d",
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              bgcolor: "#FFFFFF",
              borderLeft: "1px solid #E8ECF0",
              overflow: "hidden",
              animation: "panelIn 0.38s cubic-bezier(0.22,1,0.36,1)",
              "@keyframes panelIn": {
                from: { opacity: 0, transform: "translateX(28px)" },
                to: { opacity: 1, transform: "translateX(0)" },
              },
            }}
          >
            {/* Form header */}
            <Box
              sx={{
                px: 4,
                pt: 3,
                pb: 2.5,
                borderBottom: "1px solid #E8ECF0",
                flexShrink: 0,
                backgroundImage: `
                  linear-gradient(160deg, rgb(26, 26, 26) 0%, #00000027 80%),
                  url(${patternIng})
                `,
                backgroundRepeat: "no-repeat",
                backgroundSize: "contain",
                backgroundPosition: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.62rem",
                      fontWeight: 700,
                      letterSpacing: "0.14em",
                      color: "#ebba1c",
                      mb: 0.6,
                      textTransform: "uppercase",
                    }}
                  >
                    {editMode ? "Editing Configuration" : "New Prefix Config"}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "1.35rem",
                      fontWeight: 800,
                      color: "#ffffff",
                      letterSpacing: "-0.02em",
                      lineHeight: 1.15,
                    }}
                  >
                    {editMode
                      ? `${
                          matrixData.find((m) => m.id === formData.metalTypeId)
                            ?.name || "Edit"
                        } Prefix`
                      : "Add Prefix Configuration"}
                  </Typography>
                  {editMode && formData.id && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        mt: 0.8,
                        px: 1,
                        py: 0.3,
                        bgcolor: "rgba(26,26,26,0.05)",
                        borderRadius: "6px",
                        border: "1px solid #D1D9E0",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          color: "#D4AF37",
                          fontFamily: "monospace",
                        }}
                      >
                        ID #{formData.id}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <IconButton
                  onClick={closeForm}
                  sx={{
                    bgcolor: "#ffffff",
                    border: "1px solid #D4AF37",
                    borderRadius: "9px",
                    p: "7px",
                    "&:hover": { bgcolor: "#F7F8FA", borderColor: "#D1D9E0" },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16, color: "#D4AF37" }} />
                </IconButton>
              </Box>
            </Box>

            {/* Scrollable body */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                overflowX: "hidden",
                px: 4,
                py: 3,
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              <SectionHead
                label="Configuration Target"
                description="Select the metal type and transaction prefix for this configuration"
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2,
                  mb: 2.5,
                }}
              >
                <Box>
                  <Label required>Metal Type</Label>
                  <Select
                    value={formData.metalTypeId}
                    onChange={(e) => set("metalTypeId", e.target.value)}
                    size="small"
                    fullWidth
                    displayEmpty
                    sx={selectSx}
                    MenuProps={menuPropsSx}
                    disabled={editMode}
                  >
                    <MenuItem value="" disabled>
                      Select Metal Type
                    </MenuItem>
                    {matrixData.map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.72rem",
                              color: "#9AA3B2",
                            }}
                          >
                            {m.code}
                          </Typography>
                          <Typography sx={{ fontSize: "0.83rem" }}>
                            {m.name}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box>
                  <Label required>Prefix Type</Label>
                  <Select
                    value={formData.prefixMasterId}
                    onChange={(e) => set("prefixMasterId", e.target.value)}
                    size="small"
                    fullWidth
                    displayEmpty
                    sx={selectSx}
                    MenuProps={menuPropsSx}
                    disabled={editMode}
                  >
                    <MenuItem value="" disabled>
                      Select Prefix Type
                    </MenuItem>
                    {allPrefixMasters.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>

              <SectionHead
                label="Prefix Pattern"
                description="Define how document numbers will be formatted"
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr",
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box>
                  <Label required>Prefix Value</Label>
                  <TextField
                    value={formData.prefixValue}
                    onChange={(e) => set("prefixValue", e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="e.g. SB"
                    sx={monoFieldSx}
                  />
                </Box>
                <Box>
                  <Label>Separator</Label>
                  <Select
                    value={formData.separator}
                    onChange={(e) => set("separator", e.target.value)}
                    size="small"
                    fullWidth
                    sx={selectSx}
                    MenuProps={menuPropsSx}
                  >
                    {SEPARATOR_OPTIONS.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s === "-"
                          ? "Dash (-)"
                          : s === "/"
                          ? "Slash (/)"
                          : s === "_"
                          ? "Underscore (_)"
                          : "Dot (.)"}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box>
                  <Label>Serial Length</Label>
                  <TextField
                    value={formData.serialLength}
                    onChange={(e) =>
                      set("serialLength", Number(e.target.value))
                    }
                    fullWidth
                    size="small"
                    type="number"
                    sx={fieldSx}
                    inputProps={{ min: 3, max: 10 }}
                  />
                </Box>
              </Box>

              <SectionHead
                label="Financial Year Settings"
                description="Configure how financial year is incorporated into numbering"
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box>
                  <Label>FY Format</Label>
                  <Select
                    value={formData.fyFormat}
                    onChange={(e) => set("fyFormat", e.target.value)}
                    size="small"
                    fullWidth
                    sx={selectSx}
                    MenuProps={menuPropsSx}
                  >
                    {FY_FORMAT_OPTIONS.map((f) => (
                      <MenuItem key={f} value={f}>
                        {f}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box>
                  <Label>Current FY</Label>
                  <Select
                    value={formData.currentFy}
                    onChange={(e) => set("currentFy", e.target.value)}
                    size="small"
                    fullWidth
                    sx={selectSx}
                    MenuProps={menuPropsSx}
                  >
                    {FY_OPTIONS.map((fy) => (
                      <MenuItem key={fy} value={fy}>
                        {fy}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-end" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      py: 1,
                      bgcolor: "#F7F8FA",
                      borderRadius: "10px",
                      border: "1.5px solid #E8ECF0",
                      width: "100%",
                    }}
                  >
                    <Switch
                      checked={formData.includeFy}
                      onChange={(e) => set("includeFy", e.target.checked)}
                      size="small"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#1a1a1a",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          { bgcolor: "#1a1a1a" },
                      }}
                    />
                    <Typography sx={{ fontSize: "0.75rem", color: "#5A6478" }}>
                      Include FY
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    bgcolor: "#F7F8FA",
                    borderRadius: "10px",
                    border: "1.5px solid #E8ECF0",
                  }}
                >
                  <Switch
                    checked={formData.resetOnFy}
                    onChange={(e) => set("resetOnFy", e.target.checked)}
                    size="small"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#1a1a1a",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        { bgcolor: "#1a1a1a" },
                    }}
                  />
                  <Typography sx={{ fontSize: "0.75rem", color: "#5A6478" }}>
                    Reset serial on FY change
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    bgcolor: "#F7F8FA",
                    borderRadius: "10px",
                    border: "1.5px solid #E8ECF0",
                  }}
                >
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => set("isActive", e.target.checked)}
                    size="small"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#1a1a1a",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        { bgcolor: "#1a1a1a" },
                    }}
                  />
                  <Typography sx={{ fontSize: "0.75rem", color: "#5A6478" }}>
                    Active configuration
                  </Typography>
                </Box>
              </Box>

              <SectionHead
                label="Preview"
                description="Live preview of the next generated document number"
              />

              <Box
                sx={{
                  p: 3,
                  bgcolor: "#FDFBF0",
                  borderRadius: "14px",
                  border: "2px solid #D4AF37",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      color: "#D4AF37",
                      letterSpacing: "0.1em",
                      mb: 0.8,
                      textTransform: "uppercase",
                    }}
                  >
                    Next Document Number
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "1.5rem",
                      fontWeight: 800,
                      color: "#1a1a1a",
                      fontFamily: "monospace",
                      letterSpacing: "0.02em",
                    }}
                  >
                    {buildPreview(formData)}
                  </Typography>
                </Box>
                <Tooltip title="Copy to clipboard">
                  <IconButton
                    onClick={() =>
                      navigator.clipboard.writeText(buildPreview(formData))
                    }
                    sx={{
                      bgcolor: "#FFFFFF",
                      border: "1px solid #D4AF37",
                      "&:hover": {
                        bgcolor: "#1a1a1a",
                        "& svg": { color: "#D4AF37" },
                      },
                    }}
                  >
                    <CopyIcon
                      sx={{
                        fontSize: 18,
                        color: "#D4AF37",
                        transition: "color 0.15s",
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ height: 20 }} />
            </Box>

            {/* Footer */}
            <Box
              sx={{
                px: 4,
                py: 2.5,
                borderTop: "1px solid #E8ECF0",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                bgcolor: "#F7F8FA",
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Button
                  onClick={closeForm}
                  disabled={saving}
                  sx={{
                    border: "1.5px solid #E8ECF0",
                    color: "#5A6478",
                    textTransform: "none",
                    fontWeight: 700,
                    px: 2.5,
                    py: 0.9,
                    borderRadius: "10px",
                    bgcolor: "#FFFFFF",
                    fontSize: "0.8rem",
                    "&:hover": {
                      bgcolor: "#F7F8FA",
                      borderColor: "#D1D9E0",
                      color: "#0F1724",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  endIcon={
                    saving ? (
                      <CircularProgress size={14} sx={{ color: "#D4AF37" }} />
                    ) : (
                      <ArrowIcon sx={{ fontSize: "17px !important" }} />
                    )
                  }
                  sx={{
                    background:
                      "linear-gradient(135deg, #1a1a1a 0%, #000000 100%)",
                    color: "#D4AF37",
                    px: 3,
                    py: 0.9,
                    borderRadius: "10px",
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    textTransform: "none",
                    boxShadow: "0 4px 16px rgba(26,26,26,0.3)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
                      boxShadow: "0 6px 20px rgba(26,26,26,0.4)",
                    },
                    "&.Mui-disabled": { opacity: 0.6 },
                    transition: "all 0.2s",
                  }}
                >
                  {saving
                    ? "Saving…"
                    : editMode
                    ? "Save Changes"
                    : "Create Configuration"}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            borderRadius: "12px",
            fontWeight: 600,
            fontSize: "0.82rem",
            "& .MuiAlert-icon": { fontSize: 18 },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
